/**
 * Rechnungsnummern und Rechnungsdaten: Normalisierung (WS-05), Dubletten (WS-06),
 * Monat in der Nummer ↔ Rechnungsdatum (NR-06), Rechnungs- und Leistungsdatum (NR-07 … NR-13).
 */
import { isLive } from "./invoice-facts.js";
import { addDays, diffDays, isMonthKey, isWeekend, monthOf, nextWorkingDay } from "./periods.js";
import type { Cents, Finding, InvoiceNumberScheme, InvoiceRecord, IsoDate, MonthKey } from "./types.js";

// ---------------------------------------------------------------------------
// Normalisierung (WS-05, NR-01)
// ---------------------------------------------------------------------------

/**
 * Großschreibung, Zerlegung an Nicht-Alphanumerischem, führende Nullen je Segment weg,
 * Segmente mit „-“ verbunden. „re 2026 007“ = „RE-2026-7“; „RE-1-2“ ≠ „RE-12“.
 */
export function normalizeInvoiceNumber(input: string): string {
  return input
    .toUpperCase()
    .split(/[^0-9A-Z]+/)
    .filter(Boolean)
    .map((seg) => seg.replace(/^0+(?=[0-9])/, "")) // nur vor Ziffern, gleichlautend mit app.norm_invoice_number
    .join("-");
}

// ---------------------------------------------------------------------------
// Dubletten (WS-06, NR-02 … NR-05, RE-40)
// ---------------------------------------------------------------------------

export interface NumberConflictResult {
  /** true = frei; false = echte Dublette oder leere Nummer, wird blockiert (keine Rückfrage) */
  ok: boolean;
  conflicts: InvoiceRecord[];
  /** Nummer ist leer oder besteht nur aus Trennzeichen (R-25) */
  empty: boolean;
}

/**
 * Eine Nummer ist je Aussteller eindeutig. Nur verworfene oder zurückgezogene Rechnungen geben
 * ihre Nummer frei; die Korrektur DERSELBEN Rechnung (gleiche ID) behält ihre Nummer.
 */
export function checkNumberConflict(
  candidate: { number: string; issuerId: string; invoiceId?: string | null },
  existing: readonly InvoiceRecord[],
): NumberConflictResult {
  const n = normalizeInvoiceNumber(candidate.number);
  if (!n) return { ok: false, conflicts: [], empty: true };
  const conflicts = existing.filter(
    (e) =>
      e.issuerId === candidate.issuerId &&
      e.id !== candidate.invoiceId &&
      isLive(e) &&
      !!e.number &&
      normalizeInvoiceNumber(e.number) === n,
  );
  return { ok: conflicts.length === 0, conflicts, empty: false };
}

/** NR-05: Doppelvergaben je Aussteller (für die Agentur-Ansicht). */
export function findDuplicateNumberGroups(invoices: readonly InvoiceRecord[]): { issuerId: string; number: string; invoiceIds: string[] }[] {
  const groups = new Map<string, { issuerId: string; number: string; invoiceIds: string[] }>();
  for (const inv of invoices) {
    if (!inv.number || !isLive(inv)) continue;
    const n = normalizeInvoiceNumber(inv.number);
    const key = `${inv.issuerId}\u0000${n}`;
    const g = groups.get(key) ?? { issuerId: inv.issuerId, number: n, invoiceIds: [] };
    g.invoiceIds.push(inv.id);
    groups.set(key, g);
  }
  return [...groups.values()].filter((g) => g.invoiceIds.length > 1);
}

/**
 * RE-40: gleiche Leistung doppelt abgerechnet (gleicher Deal UND gleicher Leistungsmonat,
 * verworfene/zurückgezogene ausgenommen). Ergebnis: Rechnungs-ID → Gegenrechnungen.
 */
export function findSameServiceDuplicates(invoices: readonly InvoiceRecord[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();
  for (const inv of invoices) {
    if (!isLive(inv) || !inv.dealId || !inv.revenueMonth) continue;
    const key = `${inv.dealId}\u0000${inv.revenueMonth}`;
    groups.set(key, [...(groups.get(key) ?? []), inv.id]);
  }
  const out = new Map<string, string[]>();
  for (const ids of groups.values()) {
    if (ids.length < 2) continue;
    for (const id of ids) out.set(id, ids.filter((x) => x !== id));
  }
  return out;
}

// ---------------------------------------------------------------------------
// Monat in der Nummer (NR-06)
// ---------------------------------------------------------------------------

const YEAR = "(20\\d{2})";
const MONTH2 = "(0[1-9]|1[0-2])";

/**
 * Muster-Treffer und Monat getrennt: ein Muster ohne {YYYY}/{MM} passt, liefert aber keinen Monat (R-03).
 * Tokens wie bei der Vergabe in der Datenbank (0037, app.format_invoice_number): {P} Präfix, {YYYY} Jahr, {YY} Jahr zweistellig,
 * {MM} Monat, {N} laufende Nummer, {N:k} laufende Nummer mit mindestens k Stellen (1–8; längere Nummern werden nie gekürzt).
 */
function matchTemplate(num: string, template: string): { matched: boolean; months: MonthKey[] } {
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  let rx = "";
  const order: ("Y" | "YY" | "M")[] = [];
  for (const part of template.split(/(\{YYYY\}|\{YY\}|\{MM\}|\{N(?::[1-8])?\}|\{P\})/)) {
    if (part === "{YYYY}") {
      rx += "(\\d{4})";
      order.push("Y");
    } else if (part === "{YY}") {
      rx += "(\\d{2})";
      order.push("YY");
    } else if (part === "{MM}") {
      rx += "(\\d{2})";
      order.push("M");
    } else if (/^\{N(?::[1-8])?\}$/.test(part)) {
      const k = part.length > 3 ? Number(part.slice(3, -1)) : 1;
      rx += `\\d{${k},}`;
    } else if (part === "{P}") rx += "[A-Za-z0-9]*";
    else rx += esc(part);
  }
  const m = new RegExp(`^${rx}$`, "i").exec(num.trim());
  if (!m) return { matched: false, months: [] };
  const at = (k: "Y" | "YY" | "M") => (order.includes(k) ? m[order.indexOf(k) + 1] : undefined);
  const y = at("Y") ?? (at("YY") ? `20${at("YY")}` : undefined);
  const mo = at("M");
  if (!y || !mo) return { matched: true, months: [] };
  const key = `${y}-${mo}`;
  return { matched: true, months: isMonthKey(key) ? [key] : [] };
}

/**
 * Monatsangaben in einer Nummer erkennen: 2026-07, 2026/07, 07-2026, 07/2026, 202607.
 * Monate brauchen zwei Ziffern; angrenzende Ziffern schließen einen Treffer aus.
 */
export function detectMonthsInNumber(num: string, scheme?: InvoiceNumberScheme): MonthKey[] {
  if (scheme?.template) return matchTemplate(num, scheme.template).months;
  const found = new Set<MonthKey>();
  const patterns: { rx: RegExp; y: number; m: number }[] = [
    { rx: new RegExp(`(?<!\\d)${YEAR}[-/._ ]${MONTH2}(?!\\d)`, "g"), y: 1, m: 2 },
    { rx: new RegExp(`(?<!\\d)${MONTH2}[-/._ ]${YEAR}(?!\\d)`, "g"), y: 2, m: 1 },
    { rx: new RegExp(`(?<!\\d)${YEAR}${MONTH2}(?!\\d)`, "g"), y: 1, m: 2 },
    { rx: new RegExp(`(?<!\\d)${YEAR}${MONTH2}(?:0[1-9]|[12]\\d|3[01])(?!\\d)`, "g"), y: 1, m: 2 },
  ];
  for (const p of patterns) {
    for (const m of num.matchAll(p.rx)) found.add(`${m[p.y]}-${m[p.m]}`);
  }
  return [...found];
}

/**
 * NR-06: Trägt die Nummer einen Monat, soll er zum Rechnungsdatum passen. Schema 'off' prüft nichts (Produkt-Standard seit
 * 0105, G-01). 'detect' fragt nach (question): Viele Freiberufler nummerieren „Jahr-laufende Nummer“ („2026-05“ ist dann die
 * fünfte Rechnung, kein Mai), das darf das Einreichen nicht sperren. Nur 'strict' (ausdrücklich von der Agentur gewählt)
 * sperrt (error).
 *
 * Das Nummernmuster (`scheme.template`) ist das Muster der AGENTUR für ihre eigenen Rechnungen. Es gilt nur mit
 * `applyTemplate` (Nummer der Agentur, etwa beim Übernehmen); Nummern von Creatorn werden nie am Agenturmuster gemessen
 * (Probelauf H2), dort erkennt die Prüfung Monatsangaben frei. Mit Muster gilt dieselbe Schwere für „folgt nicht dem Muster“.
 */
export function checkNumberMonth(num: string, invoiceDate: IsoDate, scheme: InvoiceNumberScheme, opts: { applyTemplate?: boolean } = {}): Finding | null {
  if (scheme.monthCheck !== "detect" && scheme.monthCheck !== "strict") return null;
  const severity = scheme.monthCheck === "strict" ? "error" : "question";
  const template = opts.applyTemplate ? scheme.template : undefined;
  let months: MonthKey[];
  if (template) {
    const t = matchTemplate(num, template);
    if (!t.matched) return { code: "NUMBER_DOES_NOT_MATCH_SCHEME", severity, params: { template } };
    months = t.months;
  } else {
    months = detectMonthsInNumber(num);
  }
  if (months.length === 0) return null;
  const month = monthOf(invoiceDate);
  if (months.includes(month)) return null;
  return { code: "NUMBER_MONTH_MISMATCH", severity, params: { numberMonth: months.join(","), invoiceMonth: month } };
}

/** Folgt eine Nummer dem Nummernmuster der Agentur ({P}, {YYYY}, {YY}, {MM}, {N}, {N:k})? */
export function numberMatchesTemplate(num: string, template: string): boolean {
  return matchTemplate(num, template).matched;
}

// ---------------------------------------------------------------------------
// Rechnungsdatum (NR-07, NR-11)
// ---------------------------------------------------------------------------

/**
 * NR-07: Bei Nutzereingabe wird ein Datum am Wochenende oder an einem Feiertag (`holidays`, Region der Agentur; SAAS-5) auf den
 * folgenden Werktag gelegt (mit Hinweis), sofern die Agentur das eingestellt hat; Importe bleiben unverändert.
 * NR-11: Hinweis bei > 2 Tagen in der Zukunft oder > 60 Tagen zurück.
 */
export function checkInvoiceDate(input: {
  invoiceDate: IsoDate;
  today: IsoDate;
  source: "user" | "import";
  workdaysOnly: boolean;
  holidays?: IsoDate[];
}): { invoiceDate: IsoDate; findings: Finding[] } {
  const findings: Finding[] = [];
  let date = input.invoiceDate;
  if (input.source === "user" && input.workdaysOnly && (isWeekend(date) || (input.holidays ?? []).includes(date))) {
    const moved = nextWorkingDay(date, input.holidays ?? []);
    findings.push({ code: "INVOICE_DATE_MOVED_TO_WORKDAY", severity: "hint", params: { from: date, to: moved } });
    date = moved;
  }
  const d = diffDays(input.today, date);
  if (d > 2) findings.push({ code: "INVOICE_DATE_IN_FUTURE", severity: "hint", params: { days: d } });
  if (d < -60) findings.push({ code: "INVOICE_DATE_OLDER_THAN_60_DAYS", severity: "hint", params: { days: -d } });
  return { invoiceDate: date, findings };
}

// ---------------------------------------------------------------------------
// Leistungsdatum / Leistungszeitraum (NR-09, NR-10)
// ---------------------------------------------------------------------------

/**
 * Leistungsdatum oder -zeitraum prüfen. Pflicht; Zukunft oder außerhalb des abgerechneten
 * Monats → eine Rückfrage beim Einreichen (Entwürfe fragen nicht, bereits bestätigt fragt nicht
 * erneut). Wochenenden sind als Leistungsdatum erlaubt.
 */
export function checkServicePeriod(input: {
  serviceDate?: IsoDate | null;
  servicePeriod?: { from: IsoDate; to: IsoDate } | null;
  invoiceDate: IsoDate | null;
  billedMonth: MonthKey | null;
  today: IsoDate;
  isDraft: boolean;
  alreadyConfirmed?: boolean;
}): Finding[] {
  const out: Finding[] = [];
  const from = input.servicePeriod?.from ?? input.serviceDate ?? null;
  const to = input.servicePeriod?.to ?? input.serviceDate ?? null;
  // Entwürfe schweigen (NR-09): fehlendes Leistungsdatum ist dort nur ein Hinweis (R-29)
  if (!from || !to) return [{ code: "SERVICE_DATE_REQUIRED", severity: input.isDraft ? "hint" : "error" }];
  if (from > to) return [{ code: "SERVICE_PERIOD_INVERTED", severity: "error" }];
  if (input.isDraft) return out;

  const outside = !!input.billedMonth && (monthOf(from) !== input.billedMonth || monthOf(to) !== input.billedMonth);
  if (!input.alreadyConfirmed) {
    if (to > input.today) out.push({ code: "SERVICE_DATE_IN_FUTURE", severity: "question" });
    else if (outside) out.push({ code: "SERVICE_DATE_OUTSIDE_BILLED_MONTH", severity: "question", params: { billedMonth: input.billedMonth! } });
  }
  // NR-10: Leistungs- und Rechnungsdatum dicht beieinander, Leistung aber nicht im Monat → Hinweis
  if (input.invoiceDate && outside) {
    const gap = diffDays(to, input.invoiceDate);
    if (gap >= 0 && gap <= 3) out.push({ code: "SERVICE_DATE_NEAR_INVOICE_DATE", severity: "hint" });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Budget-Abweichung (NR-13) und Entwurf wiederverwenden (NR-12)
// ---------------------------------------------------------------------------

/** NR-13: Positionssumme weicht mehr als `thresholdPercent` vom Deal-Wert ab → Hinweis. */
export function checkBudgetDeviation(linesNetCents: Cents, dealValueCents: Cents | null, thresholdPercent: number): Finding | null {
  if (!dealValueCents || dealValueCents <= 0) return null;
  const devBp = Math.round((Math.abs(linesNetCents - dealValueCents) * 10_000) / dealValueCents);
  if (devBp <= Math.round(thresholdPercent * 100)) return null;
  return { code: "BUDGET_DEVIATION", severity: "hint", params: { percent: devBp / 100, threshold: thresholdPercent } };
}

export interface DraftFields {
  number: string | null;
  invoiceDate: IsoDate | null;
  serviceDate: IsoDate | null;
  subject: string | null;
  dealId: string | null;
  recipientId: string | null;
  lines: unknown[];
}

/**
 * NR-12: Beim Laden eines Entwurfs werden Nummer, Rechnungsdatum, Leistungsdatum und Betreff
 * geleert; wechselt der Deal, zusätzlich alle dealgebundenen Felder.
 */
export function resetDraftForReuse<T extends DraftFields>(draft: T, targetDealId: string | null): T {
  const base: T = { ...draft, number: null, invoiceDate: null, serviceDate: null, subject: null };
  if (targetDealId !== draft.dealId) return { ...base, dealId: targetDealId, recipientId: null, lines: [] };
  return base;
}

// Fälligkeit aus Rechnungsdatum + Frist: periods.dueDateFrom (eine Funktion für alle, R-23)
